import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { TextField, Checkbox, FormControlLabel, Box, Typography } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';

const normalizeForSearch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

const OPTION_HEIGHT = 36;
const SEARCH_DEBOUNCE_MS = 250;
const OVERSCAN_ROWS = 6;

interface CheckboxListPickerProps {
    values: FieldValue[];
    selectedValues: string[];
    onToggleOption: (optionName: string, checked: boolean) => void;
    onToggleSelectAll: (allSelected: boolean) => void;
    searchPlaceholder: string;
    selectAllLabel: string;
    noOptionsLabel: string;
    selectedCountLabel: (count: number) => string;
    maxVisibleRows?: number;
    fillHeight?: boolean;
}

function CheckboxListPicker({
    values,
    selectedValues,
    onToggleOption,
    onToggleSelectAll,
    searchPlaceholder,
    selectAllLabel,
    noOptionsLabel,
    selectedCountLabel,
    maxVisibleRows = 5,
    fillHeight = false,
}: CheckboxListPickerProps) {
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scrollBoxRef = useRef<HTMLDivElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);

    useEffect(() => {
        const box = scrollBoxRef.current;
        if (!box || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => setViewportHeight(entry.contentRect.height));
        observer.observe(box);
        return () => observer.disconnect();
    }, []);

    const handleSearchChange = (newInputValue: string) => {
        setInputValue(newInputValue);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(newInputValue);
            if (scrollBoxRef.current) scrollBoxRef.current.scrollTop = 0;
            setScrollTop(0);
        }, SEARCH_DEBOUNCE_MS);
    };

    const usableValues = useMemo(() => values.filter(option => option.name && option.label.trim()), [values]);

    const filteredValues = useMemo(() => {
        const query = normalizeForSearch(search);
        if (!query) return usableValues;
        return usableValues.filter(option => normalizeForSearch(option.label).includes(query));
    }, [usableValues, search]);

    const allSelected = usableValues.length > 0 && selectedValues.length === usableValues.length;
    const showSelectAllRow = !inputValue && usableValues.length > 0;

    const scrollRafRef = useRef<number | null>(null);
    const latestScrollTopRef = useRef(0);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        latestScrollTopRef.current = e.currentTarget.scrollTop;
        if (scrollRafRef.current !== null) return;
        scrollRafRef.current = requestAnimationFrame(() => {
            scrollRafRef.current = null;
            setScrollTop(latestScrollTopRef.current);
        });
    };

    const optionRowSx = { display: 'flex', height: OPTION_HEIGHT, minHeight: OPTION_HEIGHT, m: 0, px: 1 };

    const headerHeight = showSelectAllRow ? OPTION_HEIGHT : 0;
    const contentScrollTop = Math.max(0, scrollTop - headerHeight);
    const startIndex = Math.max(0, Math.floor(contentScrollTop / OPTION_HEIGHT) - OVERSCAN_ROWS);
    const visibleRowCount = Math.ceil((viewportHeight || OPTION_HEIGHT * maxVisibleRows) / OPTION_HEIGHT) + OVERSCAN_ROWS * 2;
    const endIndex = Math.min(filteredValues.length, startIndex + visibleRowCount);
    const visibleValues = filteredValues.slice(startIndex, endIndex);
    const topSpacerHeight = startIndex * OPTION_HEIGHT;
    const bottomSpacerHeight = (filteredValues.length - endIndex) * OPTION_HEIGHT;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: fillHeight ? '100%' : undefined }}>
            {values.length > 0 && (
                <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600 }}>
                    {selectedCountLabel(selectedValues.length)}
                </Typography>
            )}
            {values.length > 0 && (
                <TextField
                    size="small"
                    placeholder={searchPlaceholder}
                    value={inputValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
            )}
            <Box
                ref={scrollBoxRef}
                onScroll={handleScroll}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    ...(fillHeight ? { flexGrow: 1 } : { maxHeight: OPTION_HEIGHT * maxVisibleRows }),
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                }}
            >
                {showSelectAllRow && (
                    <FormControlLabel
                        sx={optionRowSx}
                        control={
                            <Checkbox
                                checked={allSelected}
                                onChange={() => onToggleSelectAll(allSelected)}
                                size="small"
                            />
                        }
                        label={selectAllLabel}
                    />
                )}
                {filteredValues.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6c757d', px: 1, py: 1 }}>
                        {noOptionsLabel}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ height: topSpacerHeight, flexShrink: 0 }} />
                        {visibleValues.map((option, idx) => (
                            <FormControlLabel
                                key={startIndex + idx}
                                sx={optionRowSx}
                                control={
                                    <Checkbox
                                        checked={selectedSet.has(option.name)}
                                        onChange={(e) => onToggleOption(option.name, e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                        <Box sx={{ height: bottomSpacerHeight, flexShrink: 0 }} />
                    </>
                )}
            </Box>
        </Box>
    );
}

export default CheckboxListPicker;
